import { NmLoader } from '../NmLoader';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CalendarHealthDocument, HealthDocumentSavedMetric } from '../../services/api';
import { healthDocuments } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../NmConfirmDialog';
import { MobileSheet } from '../MobileSheet';
import { NmButton } from '../NmButton';
import { presentHealthMetricName } from '../../utils/healthMetricPresentation';

interface Props {
  doc: CalendarHealthDocument;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

const HEALTH_COLOR = 'var(--palette-brand)';

export function HealthDocumentDetailSheet({ doc, open, onClose, onDeleted }: Props) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [metrics, setMetrics] = useState<HealthDocumentSavedMetric[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsFailed, setMetricsFailed] = useState(false);

  const typeLabel = t(`calendar.healthDocument.testType.${doc.test_type ?? 'unknown'}`, {
    defaultValue: doc.test_type ?? t('calendar.healthDocument.testType.unknown', 'Health report'),
  });

  useEffect(() => {
    if (!open || doc.saved_metrics_count <= 0) return;
    let active = true;
    setMetricsLoading(true);
    setMetricsFailed(false);
    void healthDocuments
      .getStatus(doc.id)
      .then((response) => {
        if (active) setMetrics(response.data.data?.saved_metrics ?? []);
      })
      .catch(() => {
        if (active) setMetricsFailed(true);
      })
      .finally(() => {
        if (active) setMetricsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [doc.id, doc.saved_metrics_count, open]);

  const handleViewFile = useCallback(async () => {
    if (!doc.has_file) {
      showToast(t('calendar.healthDocument.fileMissing', 'File is no longer available'));
      return;
    }
    setLoading(true);
    try {
      const blob = await healthDocuments.downloadFile(doc.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      showToast(t('calendar.healthDocument.fileOpenFailed', 'Could not open file'));
    } finally {
      setLoading(false);
    }
  }, [doc.has_file, doc.id, showToast, t]);

  const handleDelete = useCallback(async () => {
    const ok = await confirm({
      title: t('calendar.confirm.deleteHealthDocumentTitle', 'Delete health report'),
      message: t(
        'calendar.confirm.deleteHealthDocumentMessage',
        'Delete this report, its file, and all metrics saved from it?',
      ),
      variant: 'danger',
    });
    if (!ok) return;

    setDeleting(true);
    try {
      await healthDocuments.destroy(doc.id);
      showToast(t('calendar.toast.healthDocumentDeleted', 'Health report deleted'));
      onClose();
      onDeleted?.();
    } catch {
      showToast(t('calendar.toast.healthDocumentDeleteFailed', 'Failed to delete health report'));
    } finally {
      setDeleting(false);
    }
  }, [confirm, doc.id, onClose, onDeleted, showToast, t]);

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      zIndex={14500}
      title={typeLabel}
      subtitle={doc.original_filename}
      sheetClassName="health-doc-sheet"
      footer={
        <div className="health-doc-sheet__actions">
          {doc.has_file ? (
            <NmButton
              variant="primary"
              disabled={loading || deleting}
              onClick={() => void handleViewFile()}
              style={{ width: '100%', background: HEALTH_COLOR, borderColor: HEALTH_COLOR }}
            >
              {loading
                ? t('calendar.healthDocument.openingFile', 'Opening…')
                : t('calendar.healthDocument.viewFile', 'View file')}
            </NmButton>
          ) : (
            <p className="health-doc-sheet__muted">
              {t('calendar.healthDocument.fileMissing', 'File is no longer available')}
            </p>
          )}
          <NmButton
            variant="secondary"
            disabled={deleting || loading}
            onClick={() => void handleDelete()}
            className="health-doc-sheet__delete"
            style={{ width: '100%' }}
          >
            {deleting
              ? t('calendar.healthDocument.deleting', 'Deleting…')
              : t('calendar.healthDocument.deleteReport', 'Delete report')}
          </NmButton>
        </div>
      }
    >
      <p className="health-doc-sheet__meta">
        {t('calendar.healthDocument.status', {
          status: t(`calendar.healthDocument.statusValue.${doc.status}`, { defaultValue: doc.status }),
        })}
        {doc.saved_metrics_count > 0
          ? ` · ${t('calendar.healthDocument.metricsSaved', { count: doc.saved_metrics_count })}`
          : ''}
      </p>
      {doc.saved_metrics_count > 0 ? (
        <section className="health-doc-sheet__results" aria-labelledby="health-doc-results-title">
          <div className="health-doc-sheet__results-head">
            <h4 id="health-doc-results-title">{t('calendar.healthDocument.resultsTitle')}</h4>
            <span>{t('calendar.healthDocument.metricsCount', { count: doc.saved_metrics_count })}</span>
          </div>
          {metricsLoading ? (
            <div className="health-doc-sheet__results-state">
              <NmLoader size="sm" inline label={t('calendar.healthDocument.resultsLoading')} />
            </div>
          ) : metricsFailed ? (
            <div className="health-doc-sheet__results-state health-doc-sheet__results-state--error">
              {t('calendar.healthDocument.resultsFailed')}
            </div>
          ) : (
            <div className="health-doc-sheet__metric-grid">
              {metrics.map((metric, index) => (
                <div className="health-doc-sheet__metric" key={`${metric.metric_type_id}-${metric.key ?? index}`}>
                  <span dir="auto">
                    {presentHealthMetricName(metric.name || metric.key, i18n.resolvedLanguage ?? i18n.language)}
                  </span>
                  <strong dir="ltr">
                    {metric.value || '—'}
                    {metric.unit ? <small>{metric.unit}</small> : null}
                  </strong>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : null}
      {doc.status === 'failed' && doc.error_message ? (
        <p className="health-doc-sheet__error">{doc.error_message}</p>
      ) : null}
    </MobileSheet>
  );
}
